import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { IContentHeader } from "../../../../layout/components/content-header/content-header.component";
import { Subject } from "rxjs";
import { FormArray, FormGroup } from "@angular/forms";
import { InvoiceService } from "../../invoice.service";
import { ActivatedRoute, Router } from "@angular/router";
import { UtilsService } from "../../../../auth/service/utils.service";
import { takeUntil, tap } from "rxjs/operators";
import moment, { utc } from "moment/moment";
import { IInvoice } from "../../interfaces/invoice.interface";

@Component({
  selector: "app-invoice-edit",
  templateUrl: "./invoice-edit.component.html"
})
export default class InvoiceEditComponent implements OnInit, OnDestroy {
  contentHeader: IContentHeader;
  invoiceForm: FormGroup;
  invoiceId: number;
  invoice: any;
  users = [];
  products = [];
  rows_types: any = [];
  payment_measure_units: any = [];
  invoice_document_types: any = [];
  credit_note_types: any = [];
  payment_terms: any = [];
  customersList: any = [];
  document_codes: { id: number; name: string; other: { code: string } }[] = [];
  expireInDays: number = 30;
  analytics: IInvoice[] = [];
  months = moment.months();
  years = Array.from({ length: moment().year() - 2023 }, (_, i) => i + 2024);

  showErrors = false;
  usersNames: string[] = [];
  selectableOrders = [];
  parent_orders = [];
  canAddRow = true;
  clickedOnSave = false;
  searching_analytics_id = false;
  rowsToManipulate: number[] = [];

  attachments_list: any = [];
  residual_count: { analytics: number; residual: number }[] = [];
  isBlank = false;

  private _unsubscribeAll: Subject<any> = new Subject();
  private defaultRowType: number | undefined;
  private defaultServiceTrack: any;

  constructor(
    public _invoiceService: InvoiceService,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private router: Router,
    public _utilsService: UtilsService
  ) {
    this._utilsService.disableAutoHideLoading();

    this.activatedRoute.params
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((params) => {
        if (!!params.id) {
          this.invoiceId = params.id;
        }
      });

    this.contentHeader = {
      headerTitle: "EDIT_INVOICE",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/"
          },
          {
            name: "MENU.INVOICES",
            isLink: false
          },
          {
            name: "MENU.INVOICES_GENERATED",
            isLink: true,
            link: "/dashboard/invoice/generated"
          },
          {
            name: "EDIT_INVOICE",
            isLink: false
          }
        ]
      }
    };
  }

  async ngOnInit() {
    await this._invoiceService.itemsForLists()
      .then((itemsForLists) => {
        this.users = itemsForLists.users;
        this.products = itemsForLists.products;
        this.document_codes = itemsForLists.document_codes;
        this.customersList = itemsForLists.customers;
        this.rows_types = itemsForLists.rows_types;
        this.payment_measure_units = itemsForLists.payment_measure_units;
        this.payment_terms = itemsForLists.payment_terms;
        this.invoice_document_types = itemsForLists.invoice_document_types;
        this.credit_note_types = itemsForLists.credit_note_types;

        this.defaultRowType = itemsForLists.rows_types.find((type: any) => !!type.other.default)?.id;
        this.defaultServiceTrack = itemsForLists.invoice_services.find((service: any) => !!service.other.default) || {};
      });

    this.loadInvoiceDetails();
  }

  ngOnDestroy() {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loadInvoiceDetails() {
    this._invoiceService.get(this.invoiceId)
      .pipe(
        takeUntil(this._unsubscribeAll),
        tap((invoice) => {
          this.invoiceForm = this._invoiceService.buildForm(false);
          this.invoice = invoice;
          this.isBlank = invoice.is_blank;
          return invoice;
        })
      )
      .subscribe((invoice: any) => {
        const additional_data = this._invoiceService.explodeAdditionalData(invoice.additional_data);

        const parent_order = {
          id: invoice.order_id || invoice.order_product_id,
          parent: null,
          order_number: invoice.order.number,
          description: invoice.order.description,
          order_date: invoice.order.date,
          is_product: !!invoice.order_product_id
        };

        this.selectableOrders.push(parent_order);
        this.parent_orders.push(parent_order);

        for (const child of (invoice.orders_child || [])) {
          this.selectableOrders.push({
            id: child.id,
            parent: invoice.order_id,
            order_number: child.order_number,
            description: child.order_description,
            order_date: child.order_date,
            is_product: !!invoice.order_product_id
          });
        }

        const find_row_type_product = this.rows_types.find((row: any) => row.other.use === "product");

        this.invoiceForm.patchValue({
          client_id: invoice.client.id,
          payment_terms: invoice.payment_terms.id,
          invoice_type: invoice.invoice_type.id,
          vat_code: invoice.invoice_type.vat,
          cig: invoice.cig,
          cup: invoice.cup,
          has_client_approval: invoice.has_client_approval,
          accrual_exercise: invoice.accrual_exercise,
          document_code_id: invoice.document_code_id,
          document_date: invoice.document_date,
          expiration_date: invoice.expiration_date,
          order_id: invoice.order_id,
          order_product_id: invoice.order_product_id,
          order_child_id: invoice.order_child_id,
          order_number: invoice.order.number,
          invoice_order_number: additional_data.IdDocumento,
          order_date: additional_data.Data
        });

        if (invoice.order_child_id) {
          const child = invoice.orders_child.find((child: any) => child.id === invoice.order_child_id);
          this.invoiceForm.patchValue({ order_number: child.order_number });
        }

        if (!invoice.order.number) {
          const additional_data = this._invoiceService.explodeAdditionalData(invoice.additional_data);

          this.invoiceForm.patchValue({
            order_number: additional_data.IdDocumento,
            order_date: additional_data.Data
          });
        }

        if (!invoice.order_id) {
          this.invoiceForm.get("order_id").setValidators(null);
          this.invoiceForm.get("order_number").setValidators(null);
          this.invoiceForm.get("invoice_order_number").setValidators(null);
        }

        invoice.rows.forEach((row: any, index: number) => {
          const addedRow = this._invoiceService.buildRow(index, this.defaultRowType, this.defaultServiceTrack.id);

          if (row.analytics_id && !this.residual_count.find((r: any) => r.analytics === row.analytics_id)) {
            this.residual_count = [...this.residual_count, { analytics: row.analytics_id, residual: row.invoice_residue }];
          }

          const childs = invoice.rows.filter((child: any) => child.parent_id === row.id);

          addedRow.patchValue({
            description: row.description,
            row_type: row.row_type_id,
            service_track: row.service_track_id,
            service_description: row.service_description,
            quantity: row.quantity,
            price: row.price,
            payment_measure_unit: row.payment_measure_unit_id,
            discount: row.discount || 0,
            analytics_id: row.analytics_id,
            has_parent: !!row.parent_id,
            total_amount: row.row_total_amount,
            month_name: this.months[row.month - 1],
            month: row.month,
            year: row.year,
            order_id: row.order_id,
            childs: childs.length ? childs : null,
            product_id: null,
            vat_code: row.vat_code,
            with_product: false
          });

          this.usersNames[index] = "ROW";

          if (!!row.user && !!row.user.hash) {
            this.usersNames[index] = row.user.complete_name;
            addedRow.patchValue({
              user_hash: row.user.hash
            });
          } else if (!!row.product && !!row.product.hash) {
            this.usersNames[index] = row.product.name;
            addedRow.patchValue({
              user_hash: null,
              product_id: row.product.hash,
              with_product: true
            });

            if (find_row_type_product) {
              addedRow.patchValue({ row_type: find_row_type_product.id });
            }
          } else if (childs.length) {
            this.usersNames[index] = "GROUPED_ROWS";
            const user_hash = childs.map((child: any) => !!child.user.hash ? child.user.hash : null).filter((hash: any) => !!hash);
            const product_id = childs.map((child: any) => !!child.product.hash ? child.product.hash : null).filter((hash: any) => !!hash);
            addedRow.patchValue({ user_hash, product_id, with_product: product_id.length });
          }

          this.rows.push(addedRow);
        });

        const analytics_ids = this.invoice.rows.map((row: any) => row.id);

        for (const row of this.rows.controls) {
          if (row.value.analytics_id) {
            const residue = this.residual_count.find((r) => r.analytics === row.value.analytics_id);
            residue.residual += row.value.total_amount;
          }

          if (row.value.childs) {
            row.patchValue({ childs: row.value.childs.map((child: any) => analytics_ids.indexOf(child.id)) });
          }
        }

        this.calculateTotalAmount();

        this._utilsService.hideLoading();
      });
  }

  get rows() {
    return this.invoiceForm.controls.rows as FormArray;
  }

  calculateTotalAmount() {
    this.invoiceForm.patchValue({ total_amount: this._invoiceService.calculateTotalAmount(this.rows) });
  }

  toggleSelectRow(index: number) {
    if (this.rowsToManipulate.includes(index)) {
      this.rowsToManipulate = this.rowsToManipulate.filter((item) => item !== index);
    } else {
      this.rowsToManipulate.push(index);
    }
  }

  selectAllToDelete() {
    this.rowsToManipulate = this.rows.controls.map((row) => row.value.id);
  }

  async compressInOneLine() {
    const { controls } = this.rows;

    const selectedRows: any[] = controls.filter((row) => this.rowsToManipulate.includes(Number(row.value.id)))
      .map((row) => row.value);

    if (!selectedRows.length) return false;

    const countProducts = selectedRows.filter((row: any) => !!row.product_id && !row.has_parent).length;
    const countEmployees = selectedRows.filter((row: any) => !!row.user_hash && !row.has_parent).length;

    if (countEmployees > 0 && countProducts > 0) {
      await this._utilsService.showSWALInfo({ title: "ALERT_TITLE", text: "YOU_CAN_AGGREGATE_ONLY_POTATOES_WITH_POTATOES" });
      return false;
    }

    const unit = this.payment_measure_units.find((u: any) => u?.name === "Numero");

    const newRow = this._invoiceService
      .buildMergedRow(
        this.rows.controls.length,
        this.defaultRowType,
        this.defaultServiceTrack,
        unit.id || null,
        selectedRows.map((row: any) => row.id)
      );

    if (countProducts > 0) {
      const row_type_product = this.rows_types.find((type: any) => type.other.use === "product");
      newRow.patchValue({ row_type: row_type_product.id, with_product: true });
    }

    for (const row of selectedRows) {
      // imposto la riga come figlia
      let findRow = controls.find((row2: any) => row2.value.id === row.id);
      if (!findRow) continue;
      findRow.patchValue({ has_parent: true });

      newRow.patchValue({ price: Number(newRow.value.price) + Number(row.total_amount) });

      if (!!row.user_hash) {
        newRow.patchValue({ user_hash: [...(newRow.value.user_hash || []), row.user_hash] });
      }

      if (!!row.product_id) {
        newRow.patchValue({ product_id: [...(newRow.value.product_id || []), row.product_id] });
      }
    }

    newRow.patchValue({ description: this.rows.controls[0].value.description, total_amount: newRow.value.price });

    this.usersNames.push("GROUPED_ROWS");

    this.rows.push(newRow);

    this.rowsToManipulate = [];

    this._invoiceService.openLastRow();

    setTimeout(() => this.calculateTotalAmount());
  }

  addRow(values: any = null, id: number) {
    const row = this._invoiceService.buildRow(id, this.defaultRowType, this.defaultServiceTrack.id);

    if (values) {
      values.daily_rate = Number(values.daily_rate);

      let qty = values.worked_days || values.working_days;
      if (values.payment_measure_unit?.name === "Ore") {
        qty *= 8;
        values.daily_rate /= 8;
      } else if (values.payment_measure_unit?.name === "Numero") {
        qty = 1;
      }

      values.daily_rate = values.estimate_profit / qty;

      if (!!values.hash) {
        row.patchValue({
          user_hash: values.hash,
          product_id: null,
          with_product: false
        });
      } else {
        row.patchValue({
          user_hash: null,
          product_id: values.product_id,
          with_product: true
        });

        const find_row_type_product = this.rows_types.find((row: any) => row.other.use === "product");
        if (find_row_type_product) row.patchValue({ row_type: find_row_type_product.id });
      }

      row.patchValue({
        quantity: qty,
        price: values.daily_rate,
        payment_measure_unit: Number(values.payment_measure_unit.id),
        analytics_id: values.analytics_id,
        description: values.order_description,
        total_amount: values.estimate_profit,
        month_name: this.months[values.month - 1],
        month: values.month,
        year: values.year
      });

      this.usersNames.push(values.complete_name || values.name);
    } else {
      this.usersNames.push("ROW");
    }

    if (this.parent_orders.length === 1) {
      row.patchValue({ order_id: this.parent_orders[0].id });

      if (!values && this.parent_orders[0].is_product) {
        const find_row_type_product = this.rows_types.find((row: any) => row.other.use === "product");
        if (find_row_type_product) row.patchValue({ row_type: find_row_type_product.id });
        row.patchValue({ with_product: true });
      }
    }

    if (this.years.length === 1) {
      row.patchValue({ year: this.years[0] });
    }

    row.patchValue({ row_type: this.defaultRowType, service_track: this.defaultServiceTrack.id, vat_code: this.defaultServiceTrack.other.sales_vat });

    this.rows.push(row);

    this.cdr.detectChanges();
  }

  removeRows() {
    if (!this.rowsToManipulate.length) return false;

    const controls = this.rows.controls;
    let names = JSON.parse(JSON.stringify(this.usersNames));

    this.rowsToManipulate.map((row) => {
      const findIndex = controls.findIndex((row2: any) => Number(row2.value.id) === row);
      this.rows.removeAt(findIndex);
      names = names.filter((name: string, i: number) => i !== findIndex);
    });

    this.rowsToManipulate = [];
    this.usersNames = names;

    setTimeout(() => this.calculateTotalAmount());
  }

  getAnalyticsId(data: any) {
    this.canAddRow = true;
    if (!this.residual_count.find((r: any) => r.analytics === data.analytics_id)) {
      this.residual_count = [...this.residual_count, { analytics: data.analytics_id, residual: data.estimate_profit }];
    }
  }

  fieldHasError(field: string) {
    return this._invoiceService.fieldHasError(this.invoiceForm, field, this.showErrors);
  }

  handlerChangeDocumentType() {
    this._invoiceService.handlerChangeDocumentType(this.invoiceForm, this.analytics[0]);
  }

  handlerChangePaymentTerms() {
    const paymentTerm: any = this.payment_terms.find((pt: any) => pt.id === this.invoiceForm.value.payment_terms);
    this.expireInDays = paymentTerm?.other?.day_value || 30;
    if (!this.invoiceForm.value.document_date) return;
    this.handlerChangeDocumentDate();
  }

  handlerChangeDocumentDate() {
    const date: moment.Moment = utc(this.invoiceForm.value.document_date);
    const paymentTerm: any = this.payment_terms.find((pt: any) => pt.id === this.invoiceForm.value.payment_terms);
    date.add((paymentTerm.other.day_value || this.expireInDays)/30, "months");
    if (paymentTerm && paymentTerm.other.payment_end_month) {
      date.endOf("month");
    }
    // aggiorno forzatamente input expiration_date
    setTimeout(() => {
      const boxExpireDate: any = document.querySelector("#expiration_date input");
      if (boxExpireDate) boxExpireDate.value = date.format("DD-MM-YYYY");
      this.invoiceForm.patchValue({ expiration_date: date.format("YYYY-MM-DD"), accrual_exercise: moment(this.invoiceForm.value.document_date).year() });
    });
    this.cdr.detectChanges();
  }

  async setOrder() {
    const ordNumber = this.invoiceForm.value.order_number;
    const refOrder: any = this.selectableOrders.find((order: any) => order.order_number === ordNumber);

    if (refOrder) {
      if (refOrder.parent) {
        this.invoiceForm.patchValue({
          order_id: refOrder.is_product ? null : refOrder.parent,
          order_product_id: refOrder.is_product ? refOrder.parent : null,
          order_child_id: refOrder.id,
          order_date: refOrder.order_date ? moment(refOrder.order_date).format("YYYY-MM-DD") : null
        });
      } else {
        this.invoiceForm.patchValue({
          order_id: refOrder.is_product ? null : refOrder.id,
          order_product_id: refOrder.is_product ? refOrder.id : null,
          order_child_id: null,
          order_date: refOrder.order_date ? moment(refOrder.order_date).format("YYYY-MM-DD") : null
        });
      }
      for (const row of this.rows.controls) {
        row.patchValue({ description: refOrder.description });
      }
    } else {
      const invoice = this.analytics[0];

      this.invoiceForm.patchValue({
        order_id: invoice.order_id,
        order_product_id: null,
        order_child_id: null,
        order_date: invoice.order_date ? moment(invoice.order_date).format("YYYY-MM-DD") : null
      });

      for (const row of this.rows.controls) {
        row.patchValue({ description: invoice.order_description });
      }
    }

    if (!this.analytics.length) {
      const count_rows_with_analytics = this.rows.controls.filter((r: any) => !!r.value.analytics_id).length;
      if (count_rows_with_analytics > 0) {
        for (const row of this.rows.controls) {
          row.patchValue({ analytics_id: null, order_id: null });
        }
        await this._utilsService.showToastrSuccess("INVOICES_LINES_DISCONNECTED_FROM_ANALYTICS");
      }
      const count_rows_with_order = this.rows.controls.filter((r: any) => !!r.value.order_id).length;
      if (count_rows_with_order > 0) {
        for (const row of this.rows.controls) {
          row.patchValue({ order_id: null });
        }
        await this._utilsService.showToastrSuccess("INVOICES_LINES_ORDER_RESET");
      }
    }

    this.invoiceForm.patchValue({ invoice_order_number: this.invoiceForm.value.order_number });
  }

  removeRowGrouped(values) {
    this.rows.controls.filter((row) => values.childs.includes(row.value.id))
      .map((childRow) => {
        childRow.patchValue({ has_parent: false });
      });
    const parentRowIndex = this.rows.controls.findIndex((row: any) => row.value.childs === values.childs);

    this.rows.removeAt(parentRowIndex);
    this.usersNames = this.usersNames.filter((name, i) => i !== parentRowIndex);

    this.calculateTotalAmount();
  }

  removeRow(analytics_id: number) {
    // se user è un array allora sto eliminando una riga raggruppata
    const index = this.rows.controls.findIndex((row: any) => row.value.analytics_id === analytics_id);

    this.rows.removeAt(index);
    this.usersNames = this.usersNames.filter((name, i) => i !== index);

    this.calculateTotalAmount();
  }

  handlerChangeRowType() {
    this._invoiceService.ifHaveOneOrder(this.selectableOrders, this.invoiceForm);
    this.cdr.detectChanges();
  }

  async handlerAction() {
    this.showErrors = true;
    this.clickedOnSave = true;

    if (!!this.invoiceForm.value.order_product_id) {
      this.invoiceForm.get("order_id").setValidators(null);
      this.invoiceForm.get("order_id").updateValueAndValidity();
    } else {
      this.invoiceForm.get("order_product_id").setValidators(null);
      this.invoiceForm.get("order_product_id").updateValueAndValidity();
    }

    // controllo valori
    if (this.invoiceForm.invalid) {
      for (const key of Object.keys(this.invoiceForm.controls)) {
        if (key === "rows") continue;
        if (this.invoiceForm.controls[key].status !== "VALID") {
          console.warn(`${key} is invalid`);
          const input: any = document.querySelector(`*[formcontrolname="${key}"]`);
          if (input) {
            input?.scrollIntoView({ behavior: "smooth", block: "start" });
            input?.focus();
          }
        }
      }

      for (const key of Object.keys(this.rows.controls)) {
        if (this.rows.controls[key].status !== "VALID") {
          console.warn(`${key} is invalid`);
        }
      }
      this.clickedOnSave = false;
      return false;
    }

    // controllo che non ci siano righe senza analytics id
    if (!this.isBlank) {
      const count_rows_without_analytics = this.rows.value.filter((item: any) => item.analytics_id == null && item.childs == null);
      if (count_rows_without_analytics.length > 0) {
        await this._utilsService.showToastrError("A_FEW_LINES_ARE_MISSING_THE_LINK_TO_ANALYTICS");
        return false;
      }
    }

    const order_date_field = this.invoiceForm.value.order_date
    const order_date = order_date_field ? (order_date_field.includes('/') ? order_date_field : moment(order_date_field).format('DD/MM/YYYY')) : null;

    const values: any = {
      ...this.invoiceForm.value,
      is_blank: this.isBlank,
      rows: this.invoiceForm.value.rows.filter((row: any) => row !== null).map((row: any) => ({ ...row, invoice_residue: 0 })),
      additional_data: `[DatiOrdineAcquisto|IdDocumento|${this.invoiceForm.value.invoice_order_number || 0}];[DatiOrdineAcquisto|Data|${order_date}]`,
      attachments: this.attachments_list || []
    };

    delete values.order_number;
    delete values.order_date;

    const totalMissingAmount: number = await this._invoiceService.manageResidue(values.rows, this.residual_count);

    if (totalMissingAmount > 0) {
      const confirmationText: string = await this._utilsService.translate("ALERT.MISSING_INVOICE_AMOUNT")
        .then((text: string) => text.replace("[AMOUNT]", totalMissingAmount.toFixed(2)));

      await this._utilsService.showSWALDecision({
        text: confirmationText,
        confirmButtonText: "YES",
        cancelButtonText: "NO",
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-danger"
        }
      }).then(async (result) => {
        if (result.isDismissed) {
          for (let i = 0; i < values.rows.length; i++) {
            values.rows[i].invoice_residue = 0;
          }
        }
      });
    }

    this._utilsService.showLoading();

    this._invoiceService.update(this.invoiceId, values).subscribe((res: any) => {
      if (!!res && !res.error) {
        this.router.navigateByUrl("/dashboard/invoice/generated");
      }
    });
  }

  async getClientOrders() {
    this.selectableOrders = await this._invoiceService.getClientOrders(this.invoiceForm);
    this.cdr.detectChanges();
  }
}
